<template>
  <div>
    <settings-section-title id="create" class="i-mb-4"
      >Create API Key</settings-section-title
    >
    <breadcrumbs :items="breadcrumbs"></breadcrumbs>

    <div v-if="hasEditPermissions" class="i-mt-12 create-form">
      <label
        for="key-name"
        class="i-text-subheading-2 i-text-neutral-120 i-mb-2"
        >Name</label
      >
      <b-form-input
        id="key-name"
        v-model="$v.keyName.$model"
        placeholder="Enter a name for the API Key"
        class="i-form-input"
        :class="{ error: hasAnyError($v.keyName) || keyNameExists }"
      ></b-form-input>
      <div class="i-mt-4 i-text-body-3 i-text-error-110">
        <p v-if="hasAnyError($v.keyName) && !$v.keyName.required">
          The API Key name field is required
        </p>
        <p v-if="hasAnyError($v.keyName) && !$v.keyName.maxLength">
          The API Key Name has a maximum length limit of 40 characters
        </p>
        <p v-if="keyNameExists">
          The API Key Name must be unique, another key with this name exists
        </p>
      </div>

      <label
        for="key-description"
        class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
        >Description (optional)</label
      >
      <b-form-textarea
        id="key-description"
        v-model="$v.keyDescription.$model"
        placeholder="Enter a description"
        class="i-form-textarea"
        :class="{ error: hasAnyError($v.keyDescription) }"
      ></b-form-textarea>
      <div class="i-mt-4 i-text-body-3">
        <p
          v-if="hasAnyError($v.keyDescription) && !$v.keyDescription.maxLength"
          class="i-text-error-110"
        >
          The API Key Description field has a maximum length limit of
          <!-- eslint-disable-next-line prettier/prettier -->
          150 characters
        </p>
      </div>

      <i-button
        text="Create"
        class="i-mt-12"
        size="lg"
        :disabled="loading || $v.$error || $v.$invalid || keyNameExists"
        @button-click="createKey()"
      ></i-button>
    </div>

    <b-modal
      v-if="!!newApiToken"
      centered
      :visible="!!newApiToken"
      modal-class="b-modal md"
      @hidden="closeModal"
    >
      <template slot="modal-header">
        <div class="d-flex flex-column align-items-center">
          <h4 class="i-text-heading-4 i-text-neutral-120 i-mb-2">
            API Key Created
          </h4>
          <h5 class="i-text-body-2 i-text-neutral-110 i-mb-0">
            Please copy your API key and store it in a safe place
          </h5>
        </div>
      </template>
      <div>
        <label class="i-mb-4 i-text-subheading-2 i-text-neutral-120"
          >Secret</label
        >

        <i-copy
          element-id="full_token"
          :value="newApiToken.full_token"
        ></i-copy>

        <p class="i-text-body-3 i-text-neutral-110 i-mt-4">
          Please copy this now as we cannot show it again
        </p>

        <div class="d-flex align-items-center justify-content-center i-mt-11">
          <i-button
            text="Continue"
            size="lg"
            @button-click="closeModal()"
          ></i-button>
        </div>
      </div>
      <template slot="modal-footer"> &nbsp; </template>
    </b-modal>
  </div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { iButton, iCopyInput } from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import { GENERATE_API_KEY } from '@/constants/onboardingChecklist';
export default {
  components: {
    Breadcrumbs,
    'i-copy': iCopyInput,
    'i-button': iButton,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'API Keys',
          to: { name: 'api-settings-list-keys' },
        },
        {
          text: 'Create API Key',
          active: true,
        },
      ],
      keyName: '',
      keyDescription: '',
      keyCreationMessage: '',
      loading: false,
      newApiToken: null,
      existingKeyNames: [],
    };
  },
  validations: {
    keyName: {
      required,
      maxLength: maxLength(40),
    },
    keyDescription: {
      maxLength: maxLength(150),
    },
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),

    ...mapState(['currentUser']),

    keyNameExists() {
      return (
        this.existingKeyNames && this.existingKeyNames.includes(this.keyName)
      );
    },
  },
  mounted() {
    this.existingKeyNames = this.currentUser.organisation.api_keys.reduce(
      (results, apiKeys) => {
        if (apiKeys.name) {
          results.push(apiKeys.name);
        }
        return results;
      },
      [],
    );
  },
  methods: {
    ...mapActions(['generateApiKey']),

    ...mapMutations(['addCurrentUser', 'addCompletedAction']),

    hasAnyError(validation) {
      return validation.$dirty && validation.$error;
    },

    createKey() {
      this.loading = true;

      this.generateApiKey({
        name: this.keyName,
        description: this.keyDescription,
      })
        .then((response) => {
          this.loading = false;

          const currentUserCopy = {
            ...this.currentUser,
          };

          currentUserCopy.organisation.api_keys = [
            response.data,
            ...currentUserCopy.organisation.api_keys,
          ];

          this.newApiToken = response.data;
          this.keyCreationMessage = response.data.message;

          // mutations
          this.addCurrentUser(currentUserCopy);
          this.addCompletedAction(GENERATE_API_KEY);
        })
        .catch((error) => {
          if (error.response) {
            this.$notify({
              group: 'api',
              type: 'error',
              duration: 5000,
              text: 'Error creating API Key',
            });
            this.loading = false;
          }
        });
    },
    closeModal() {
      this.newApiToken = null;
      this.$notify({
        group: 'api',
        type: 'success',
        duration: 6000,
        text: this.keyCreationMessage,
      });
      this.$router.push({
        name: 'api-settings-list-keys',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.create-form {
  max-width: 500px;
}
</style>
